import React, { useState, useEffect } from "react";
import { 
  MessageSquare, 
  Sparkles, 
  ArrowUpRight, 
  CheckCircle2, 
  Clock, 
  Zap, 
  Compass, 
  HeartHandshake,
  ExternalLink,
  Lock
} from "lucide-react";
import { useUserProfile } from "../../contexts/UserProfileContext";
import { 
  getDailyRecommendations, 
  getWhatsAppChatUrl, 
  WE360_WHATSAPP_BOT_NUMBER,
  DEMO_DAILY_RECOMMENDATIONS,
  isCannedRecommendationMessage,
  isCannedActiveNudge
} from "../../services/coreServicesClient";

/**
 * WhatsAppDailyFeedWidget - WE360
 *
 * Conectado al endpoint GET /api/user/daily-recommendations de WE360 Core Services.
 * Muestra el activeNudge (reto o empujón activo) y el historial de intervenciones
 * emitidas por los agentes de IA (Físico, Nutrición, Estrés / Emocional).
 */
export function WhatsAppDailyFeedWidget({
  profile: propProfile,
  isDemo: propIsDemo,
  userId,
  userPhone: propUserPhone,
  className = "",
}) {
  let contextValue = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    contextValue = useUserProfile();
  } catch (e) {
    // Safe outside provider
  }

  const profile = propProfile || contextValue?.profile || {};
  const isDemo = propIsDemo !== undefined ? propIsDemo : (contextValue?.isDemo ?? false);
  const userPhone = propUserPhone || profile.whatsapp || profile.telefono || profile.phone || "";
  const userEmail = profile.email || "";

  const initialData = isDemo ? DEMO_DAILY_RECOMMENDATIONS : null;
  const [recommendationsData, setRecommendationsData] = useState(initialData);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function fetchData() {
      setLoading(true);
      try {
        const data = await getDailyRecommendations(userPhone, userEmail, 3, { isDemo });
        if (isMounted) {
          if (!isDemo && data) {
            const cleanRecs = (data.recommendations || []).filter(
              (rec) => !isCannedRecommendationMessage(rec?.message || rec?.recommendation)
            );
            const cleanNudge = (data.activeNudge && !isCannedActiveNudge(data.activeNudge)) ? data.activeNudge : null;
            setRecommendationsData({
              ...data,
              recommendations: cleanRecs,
              activeNudge: cleanNudge,
            });
          } else {
            setRecommendationsData(data || (isDemo ? DEMO_DAILY_RECOMMENDATIONS : null));
          }
        }
      } catch (err) {
        console.warn("[WhatsAppDailyFeedWidget] Error fetching recommendations:", err);
        if (isMounted && isDemo) {
          setRecommendationsData(DEMO_DAILY_RECOMMENDATIONS);
        } else if (isMounted) {
          setRecommendationsData(null);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [userPhone, userEmail, isDemo]);

  const rawNudge = recommendationsData?.activeNudge;
  const activeNudge = (!isDemo && isCannedActiveNudge(rawNudge)) ? null : rawNudge;
  const rawRecommendations = recommendationsData?.recommendations || [];
  const recommendations = isDemo
    ? rawRecommendations
    : rawRecommendations.filter((rec) => !isCannedRecommendationMessage(rec?.message || rec?.recommendation));

  const getGemBadge = (rec = {}) => {
    const badgeColor = rec.badgeColor;
    const agent = rec.agent;
    const gemType = rec.gemType;

    if (badgeColor === "emerald" || gemType === "nutrition" || agent?.includes("Nutrición")) {
      return { label: agent || "Nutrición IA", bg: "bg-emerald-50 text-emerald-700 border-emerald-200" };
    }
    if (badgeColor === "blue" || gemType === "physical" || agent?.includes("Actividad")) {
      return { label: agent || "Actividad Física IA", bg: "bg-blue-50 text-blue-700 border-blue-200" };
    }
    if (badgeColor === "purple" || gemType === "stress" || agent?.includes("Estrés")) {
      return { label: agent || "Manejo de Estrés", bg: "bg-purple-50 text-purple-700 border-purple-200" };
    }
    return { label: agent || "Coach WE360", bg: "bg-sky-50 text-sky-700 border-sky-200" };
  };

  const whatsappChatUrl = getWhatsAppChatUrl(undefined, "Hola, quiero registrar mi hábito");
  const hasActiveNudge = Boolean(activeNudge && (activeNudge.isActive !== undefined ? activeNudge.isActive : !activeNudge.dismissed));
  const isLockedForRealUser = !isDemo && !hasActiveNudge && recommendations.length === 0;

  return (
    <div
      className={`bg-white/80 backdrop-blur-md border border-slate-200/80 rounded-3xl p-6 shadow-sm transition-all ${className}`}
      data-testid="whatsapp-daily-feed-widget"
    >
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
            <MessageSquare size={22} className="text-emerald-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-extrabold text-slate-900">
                Asistente de Bienestar en WhatsApp
              </h3>
              <span className="flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> En línea
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Sincronización omnicanal: registros diarios por fotos o voz y feedback en tiempo real.
            </p>
          </div>
        </div>

        <a
          href={whatsappChatUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white bg-[#25D366] hover:bg-[#20ba5a] transition-all shadow-sm shrink-0 self-start sm:self-center"
          data-testid="btn-open-whatsapp"
        >
          <MessageSquare size={14} />
          <span>Chatear en WhatsApp</span>
          <ExternalLink size={12} className="opacity-80" />
        </a>
      </div>

      {/* ── Active Nudge (Empujón Activo del Core) ── */}
      {hasActiveNudge && (
        <div
          className="mb-5 p-4 rounded-2xl border border-sky-200 bg-gradient-to-r from-sky-50/70 via-blue-50/40 to-white flex items-start gap-3.5"
          data-testid="active-nudge-banner"
        >
          <div className="w-9 h-9 rounded-xl bg-sky-500 text-white flex items-center justify-center shrink-0 shadow-sm mt-0.5">
            <Zap size={18} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-sky-800 bg-sky-100/70 px-2 py-0.5 rounded-md">
                {activeNudge.title || `Empujón Clínico Activo: ${activeNudge.topic || ""}`}
              </span>
              <span className="text-[10px] text-slate-400">
                {activeNudge.timeFormatted || (activeNudge.issuedAt ? new Date(activeNudge.issuedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "Hoy")}
              </span>
            </div>
            <p className="text-xs font-semibold text-slate-800 mt-1.5">
              {activeNudge.description || recommendations[0]?.message || recommendations[0]?.recommendation || (isDemo ? "Paseo postprandial: 10 a 15 minutos de caminata para facilitar la captación de glucosa por GLUT4." : "Mantenga sus hábitos activos para recibir recomendaciones.")}
            </p>
          </div>
        </div>
      )}

      {/* ── Timeline de Recomendaciones Multi-Agente ── */}
      <div>
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-1.5">
          <Sparkles size={13} className="text-[#00628F]" /> Últimas Recomendaciones del Coach Multi-Agente
        </h4>

        <div className="space-y-2.5">
          {recommendations.length > 0 ? (
            recommendations.map((rec, idx) => {
              const badge = getGemBadge(rec);
              const message = rec.message || rec.recommendation;
              const time = rec.timeFormatted || (rec.date ? new Date(rec.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "");
              return (
                <div
                  key={idx}
                  className="p-3.5 rounded-2xl border border-slate-100 bg-slate-50/60 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 hover:bg-white hover:border-slate-200 transition-all"
                  data-testid={`recommendation-item-${idx}`}
                >
                  <div className="flex items-start gap-2.5">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border shrink-0 ${badge.bg}`}>
                      {badge.label}
                    </span>
                    <p className="text-xs text-slate-700 leading-relaxed font-medium">
                      {message}
                    </p>
                  </div>
                  {time && (
                    <span className="text-[10px] text-slate-400 shrink-0 self-end sm:self-center">
                      {time}
                    </span>
                  )}
                </div>
              );
            })
          ) : (
            <div
              className="p-5 rounded-2xl border border-slate-200/80 bg-slate-50/70 backdrop-blur-xs text-center flex flex-col items-center"
              data-testid={isLockedForRealUser ? "whatsapp-feed-locked-state" : "whatsapp-feed-empty-state"}
            >
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-700 mb-2.5">
                <Lock size={18} />
              </div>
              <span className="text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 mb-2">
                Se desbloquea al interactuar por WhatsApp
              </span>
              <p className="text-xs text-slate-600 font-medium max-w-lg leading-relaxed mb-3">
                Aún no hay recomendaciones generadas hoy. Interactúe con su coach en WhatsApp o registre sus hábitos para recibir orientación personalizada.
              </p>
              <a
                href={whatsappChatUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white bg-[#25D366] hover:bg-[#20ba5a] transition-all shadow-xs"
              >
                <MessageSquare size={13} />
                <span>Enviar mi primer registro en WhatsApp</span>
                <ExternalLink size={11} className="opacity-80" />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default WhatsAppDailyFeedWidget;
